import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Admin
import AdminDashboard from './pages/admin/AdminDashboard';
import UniversityManagement from './pages/admin/UniversityManagement';
import AllPartners from './pages/admin/AllPartners';
import Templates from './pages/admin/Templates';
import AdminLoans from './pages/admin/AdminLoans';
import AdminMessages from './pages/admin/AdminMessages';
import Applications from './pages/admin/Applications';

// Partner
import PartnerDashboard from './pages/partner/PartnerDashboard';
import PartnerWelcome from './pages/partner/PartnerWelcome';
import CandidateForm from './pages/partner/CandidateForm';
import PartnerUniversities from './pages/partner/PartnerUniversities';
import PartnerTemplates from './pages/partner/PartnerTemplates';
import EducationLoan from './pages/partner/EducationLoan';
import PartnerMessages from './pages/partner/PartnerMessages';
import PartnerApplications from './pages/partner/PartnerApplications';

// Shared
import AllCandidates from './pages/shared/AllCandidates';
import CandidateDetail from './pages/shared/CandidateDetail';
import ApplicationDetail from './pages/shared/ApplicationDetail';
import Notifications from './pages/shared/Notifications';

// Layout
import DashboardLayout from './components/DashboardLayout';

function ProtectedRoute({ children, allowedRoles }) {
  const location = useLocation();
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <div className="w-10 h-10 border-4 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || user === false) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/partner'} replace />;
  }

  if (user.role === 'partner' && user.onboarding_required && location.pathname !== '/partner/welcome') {
    return <Navigate to="/partner/welcome" replace state={{ returnTo: location.pathname + location.search + location.hash }} />;
  }
  return <DashboardLayout>{children}</DashboardLayout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <div className="w-10 h-10 border-4 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user && user !== false) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/partner'} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      {/* No PublicRoute: the e-mailed link must work even in a browser that is still signed in. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/universities" element={<ProtectedRoute allowedRoles={['admin']}><UniversityManagement /></ProtectedRoute>} />
      <Route path="/admin/candidates" element={<ProtectedRoute allowedRoles={['admin']}><AllCandidates /></ProtectedRoute>} />
      <Route path="/admin/candidates/:id" element={<ProtectedRoute allowedRoles={['admin']}><CandidateDetail /></ProtectedRoute>} />
      <Route path="/admin/applications" element={<ProtectedRoute allowedRoles={['admin']}><Applications /></ProtectedRoute>} />
      <Route path="/admin/applications/:id" element={<ProtectedRoute allowedRoles={['admin']}><ApplicationDetail /></ProtectedRoute>} />
      <Route path="/admin/partners" element={<ProtectedRoute allowedRoles={['admin']}><AllPartners /></ProtectedRoute>} />
      <Route path="/admin/templates" element={<ProtectedRoute allowedRoles={['admin']}><Templates /></ProtectedRoute>} />
      <Route path="/admin/loans" element={<ProtectedRoute allowedRoles={['admin']}><AdminLoans /></ProtectedRoute>} />
      <Route path="/admin/messages" element={<ProtectedRoute allowedRoles={['admin']}><AdminMessages /></ProtectedRoute>} />
      <Route path="/admin/notifications" element={<ProtectedRoute allowedRoles={['admin']}><Notifications /></ProtectedRoute>} />

      {/* Partner routes */}
      <Route path="/partner/welcome" element={<ProtectedRoute allowedRoles={['partner']}><PartnerWelcome /></ProtectedRoute>} />
      <Route path="/partner" element={<ProtectedRoute allowedRoles={['partner']}><PartnerDashboard /></ProtectedRoute>} />
      <Route path="/partner/universities" element={<ProtectedRoute allowedRoles={['partner']}><PartnerUniversities /></ProtectedRoute>} />
      <Route path="/partner/candidates" element={<ProtectedRoute allowedRoles={['partner']}><AllCandidates isPartner /></ProtectedRoute>} />
      <Route path="/partner/candidates/new" element={<ProtectedRoute allowedRoles={['partner']}><CandidateForm /></ProtectedRoute>} />
      <Route path="/partner/candidates/:id/edit" element={<ProtectedRoute allowedRoles={['partner']}><CandidateForm /></ProtectedRoute>} />
      <Route path="/partner/candidates/:id" element={<ProtectedRoute allowedRoles={['partner']}><CandidateDetail /></ProtectedRoute>} />
      <Route path="/partner/templates" element={<ProtectedRoute allowedRoles={['partner']}><PartnerTemplates /></ProtectedRoute>} />
      <Route path="/partner/applications" element={<ProtectedRoute allowedRoles={['partner']}><PartnerApplications /></ProtectedRoute>} />
      <Route path="/partner/applications/:id" element={<ProtectedRoute allowedRoles={['partner']}><ApplicationDetail /></ProtectedRoute>} />
      <Route path="/partner/loans" element={<ProtectedRoute allowedRoles={['partner']}><EducationLoan /></ProtectedRoute>} />
      <Route path="/partner/messages" element={<ProtectedRoute allowedRoles={['partner']}><PartnerMessages /></ProtectedRoute>} />
      <Route path="/partner/notifications" element={<ProtectedRoute allowedRoles={['partner']}><Notifications /></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" richColors theme={theme} />;
}

function App() {
  return (
    <ThemeProvider>
    <BrowserRouter>
      <Routes>
        {/* All routes go through the auth provider */}
        <Route path="*" element={
          <AuthProvider>
            <AppRoutes />
            <ThemedToaster />
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
